import { type Theme } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import { selectedTint } from "../../theme";
import type { ShellIcon } from "./navItems";
import {
  FOOTER_LABEL_FONT_SIZE,
  NAV_LABEL_FONT_SIZE,
  NAV_ROW_HEIGHT,
  NAV_ROW_INLINE_PADDING,
} from "./layout";

export type NavRowOrientation = "vertical" | "horizontal";

/**
 * One navigation cell shared by the phone footer, the tablet rail and the
 * desktop sidebar. A single component keeps the one IA identical across
 * shells; only the geometry and label visibility differ.
 */
export function NavRow({
  icon: Icon,
  label,
  orientation,
  showLabel = true,
  selected = false,
  tooltip = false,
  testId,
  onClick,
  href,
}: {
  icon: ShellIcon;
  label: string;
  orientation: NavRowOrientation;
  /** False renders the icon-only rail/collapsed cell. */
  showLabel?: boolean;
  selected?: boolean;
  /** Rail cells carry their label in a tooltip (spec §2). */
  tooltip?: boolean;
  testId: string;
  onClick?: () => void;
  /** Export is a download link, not a route (spec §7). */
  href?: string;
}) {
  const cell = (
    <ButtonBase
      {...(href ? { component: "a", href, download: true } : { component: "button", type: "button" })}
      onClick={onClick}
      data-testid={testId}
      aria-current={selected ? "page" : undefined}
      aria-label={showLabel ? undefined : label}
      sx={(theme) => rowSx(theme, orientation, showLabel, selected)}
    >
      <Icon fontSize="small" />
      {showLabel && (
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      )}
    </ButtonBase>
  );

  return tooltip ? <Tooltip title={label}>{cell}</Tooltip> : cell;
}

/** Selection is a full-cell 10% light / 16% dark primary tint (spec §2, §8). */
const rowSx = (
  theme: Theme,
  orientation: NavRowOrientation,
  showLabel: boolean,
  selected: boolean,
) => {
  const vertical = orientation === "vertical";
  const tint = selectedTint(theme);
  return {
    display: "flex",
    minWidth: 0,
    gap: vertical ? 0.25 : 1.5,
    flexDirection: vertical ? "column" : "row",
    alignItems: "center",
    justifyContent: vertical || !showLabel ? "center" : "flex-start",
    height: NAV_ROW_HEIGHT,
    px: rowInlinePadding(orientation, showLabel),
    // The footer shares its row between the three cells; the rail/sidebar cells
    // own their full column width.
    ...(vertical
      ? { flex: "1 1 0", flexShrink: 1 }
      : { flexShrink: 0, width: showLabel ? "100%" : NAV_ROW_HEIGHT }),
    fontSize: vertical ? FOOTER_LABEL_FONT_SIZE : NAV_LABEL_FONT_SIZE,
    fontWeight: 600,
    lineHeight: 1.2,
    color: selected ? "primary.main" : "text.secondary",
    bgcolor: selected ? tint : "transparent",
    "&:hover": { bgcolor: selected ? tint : theme.palette.action.hover },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  };
};

/** Horizontal rows inset their label; footer cells stay centred (spec §8). */
function rowInlinePadding(orientation: NavRowOrientation, showLabel: boolean) {
  return orientation === "horizontal" && showLabel ? `${NAV_ROW_INLINE_PADDING}px` : 0;
}
